import { Button, Center, Group, Loader, Pagination, Select, Text, Title } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { showNotification } from '@mantine/notifications';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exportPaginatedData,
  generateCSV,
  sessionMessagesList,
  SessionMessagesListParams,
  useBot,
  useDateLocal,
  useGetFeedbackCheckbox,
  cleanAgentContent,
} from '@mygpt/app';
import { MessageDetailInfo, ratingType } from '@mygpt/types';
import FeedbackTable, { FeedbackTableItem, RatingData } from '@/components/TeachModel/FeedbackTable';
import { getRatingReasonText, getRatingType } from '@/utils/feedback';
import { confirmPartialExport } from '@/utils/exportConfirm';

type typeValue = 'all' | 'well' | 'poorly';

interface page {
  page: number;
  total: number;
  size: number;
  pages: number;
}

dayjs.extend(utc);

const TeachModel = ({
  dateRange,
  onDateRangeChange,
  setRangeDateTimeText,
}: {
  dateRange: DatesRangeValue;
  onDateRangeChange: (date: DatesRangeValue) => void;
  setRangeDateTimeText: (text: string) => void;
}) => {
  const { t } = useTranslation();
  const { botId, robotInfo } = useBot();
  const { dateLocale } = useDateLocal();
  const feedbackCheckboxs = useGetFeedbackCheckbox();

  const tabelDataRef = useRef<MessageDetailInfo[] | null>(null);
  const [typeValue, setTypeValue] = useState<typeValue | null>('all');
  const [userIdentifier, setUserIdentifier] = useState<string>('');
  const [userIdentifierList, setUserIdentifierList] = useState<{ value: string; label: string }[]>([]);

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [tableData, setTableData] = useState<FeedbackTableItem[]>([]);
  const [page, setPage] = useState<page>({
    page: 1,
    total: 0,
    pages: 1,
    size: 50,
  });

  const TypeData = [
    { value: 'all', label: t('teach_model.feedback_type.all') },
    { value: 'well', label: t('teach_model.feedback_type.well') },
    { value: 'poorly', label: t('teach_model.feedback_type.poorly') },
  ];

  const convertTableData = (data: MessageDetailInfo[]): FeedbackTableItem[] => {
    const table: FeedbackTableItem[] = [];
    data.map((item) => {
      const rating: RatingData[] = [];
      const type = getRatingType(item.rating);

      if (type) {
        if (item.session_message_metadata?.length) {
          item.session_message_metadata?.map((meta) => {
            if (meta.key !== 'value') return;
            if (!meta.value) return;
            let value: unknown;
            try {
              value = JSON.parse(meta.value.replace(/'/g, '"'));
            } catch (e) {
              console.warn('Failed to parse session_message_metadata, skipped:', meta.value, e);
              return;
            }
            if (!Array.isArray(value)) return;
            value.map((metaValue: string) => {
              rating.push({
                type,
                text: metaValue,
              });
            });
            if (!rating.length) {
              rating.push({
                type,
                text: '',
              });
            }
          });
        } else {
          rating.push({
            type,
            text: '',
          });
        }
      }

      table.push({
        id: item.id,
        faqId: item.faq_id,
        citation: item.citation || [],
        time: dayjs(item.created_at).valueOf(),
        last_feedback_time: dayjs(item.session_message_metadata?.[0]?.updated_at).valueOf(),
        question: item.question,
        answer: item.answer,
        rating: rating,
        detail: item.feedback_content || '',
        session: item.session,
      });
    });
    return table;
  };

  const getSessionMessageListParams = (customParam?: Partial<SessionMessagesListParams>) => {
    const params: SessionMessagesListParams = {
      aiId: botId,
      rating: typeValue === 'all' ? undefined : typeValue === 'well' ? '2' : '-1',
      only_rating: true,
      page: page.page,
      size: page.size,
    };

    // 添加用户标识过滤
    if (userIdentifier) {
      params.user_identifier = userIdentifier;
    }

    // 添加日期范围过滤
    if (dateRange[0] && dateRange[1]) {
      params.start_time = dayjs(dateRange[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      params.end_time = dayjs(dateRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    }

    if (customParam) {
      Object.assign(params, customParam);
    }

    return params;
  };

  const init = async (showLoading = true) => {
    if (showLoading) {
      setTableData([]);
      setTableLoading(true);
    }

    const params = getSessionMessageListParams();

    await sessionMessagesList(params)
      .then((res) => {
        const data = res.data;
        const { items, page, pages, size, total } = data;

        setPage({ page, pages, total, size });

        if (!items || !Array.isArray(items)) return;
        tabelDataRef.current = items;
        const table = convertTableData(items);
        setTableData(table);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const downloadByRating = async () => {
    setDownloadLoading(true);

    try {
      const baseParams = getSessionMessageListParams();
      const result = await exportPaginatedData<MessageDetailInfo>({
        fetchPage: async (page, size) => {
          const res = await sessionMessagesList({ ...baseParams, page, size });
          return res?.data;
        },
        onConfirmPartial: (fetched, total) => confirmPartialExport(t, fetched, total),
      });

      if (!result) return;

      if (result.partial) {
        showNotification({
          message: t('chat_history.export.partial_success', {
            fetched: result.items.length,
            total: result.total,
          }),
          color: 'yellow.5',
        });
      }

      const tableData = convertTableData(result.items);

      const label = TypeData.find((item) => item.value === typeValue)?.label;
      const time =
        dateRange[0] && dateRange[1]
          ? ` ${dayjs(dateRange[0]).format('YYYY.MM.DD')}_${dayjs(dateRange[1]).format('YYYY.MM.DD')}`
          : '';

      const fileName = `${t('teach_model.user_feedback')}_${label}${time}`;
      // 创建CSV的头部行
      const header = [
        t('teach_model.table.last_feedback_time'),
        t('teach_model.user_feedback'),
        t('teach_model.table.rating'),
        t('teach_model.table.feedback_details'),
        t('teach_model.table.question'),
        t('faqs.form.answer'),
        t('chat_history.message_detail.session_id'),
        t('chat_history.table.user'),
        t('settings.feedback.has_feedback'),
      ];

      // 将每个数据对象转换为CSV格式的一行
      const rows = tableData.map((item) => {
        // 清理答案中的 agent 特殊标签
        const answer = cleanAgentContent(item.answer || '');

        return [
          dayjs(item.time).format('YYYY-MM-DD HH:mm:ss'),
          item.rating[0].type === 'good' ? t('teach_model.feedback_type.well') : t('teach_model.feedback_type.poorly'),
          getRatingReasonText(item.rating, feedbackCheckboxs),
          item.detail || '-',
          item.question || '',
          answer,
          item.session.session_id,
          item.session.user_identifier || '-',
          item.session.transfer_to_human ? '✓' : '',
        ];
      });
      const csvData = {
        header,
        rows,
      };

      generateCSV(fileName, csvData);
    } catch (error) {
      console.error('批量获取数据失败:', error);
      showNotification({
        message: t('export_failed'),
        color: 'red.5',
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      init();
    }
  }, [page.page]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      if (page.page === 1) {
        init();
      } else {
        setPage({ page: 1, pages: page.pages, total: page.total, size: page.size });
      }
    }
  }, [typeValue, dateRange, userIdentifier]);

  useEffect(() => {
    if (tabelDataRef.current) {
      const table = convertTableData(tabelDataRef.current);
      setTableData(table);
    }
  }, [t]);

  const getUserIdentifierList = () => {
    const params = getSessionMessageListParams({ page: 1, size: 1000 });
    sessionMessagesList(params).then((res) => {
      const data = res.data;
      const { items } = data;
      if (!items || !Array.isArray(items)) return;
      const list = new Set(
        items.filter((item) => Boolean(item.session.user_identifier)).map((item) => item.session.user_identifier)
      );
      setUserIdentifierList(Array.from(list).map((item) => ({ value: item, label: item })));
    });
  };

  useEffect(() => {
    getUserIdentifierList();
  }, []);

  return (
    <div>
      <Group justify="space-between" mb={20}>
        <Title order={4} fw={500}>
          {t('chat_history.tab.feedback_title')}
        </Title>
        <Group>
          <Select
            size="sm"
            style={{ width: 150 }}
            value={userIdentifier}
            onChange={(value: string | null) => {
              setUserIdentifier(value || '');
            }}
            data={[{ value: '', label: t('chat_history.user_identifier.all') }, ...userIdentifierList]}
            searchable
          />
          <Select
            size="sm"
            style={{ width: 150 }}
            value={typeValue}
            onChange={(value: string | null) => {
              setTypeValue(value as typeValue);
            }}
            data={TypeData}
            allowDeselect={false}
          />

          <DatePickerInput
            type="range"
            locale={dateLocale}
            size="sm"
            value={dateRange}
            onChange={(value) => {
              onDateRangeChange(value);
              setRangeDateTimeText(t('dashboard.date.custom_date'));
            }}
            valueFormat="YYYY-MM-DD"
            numberOfColumns={2}
            allowSingleDateInRange
          />

          <Button loading={downloadLoading} disabled={!tableLoading && !tableData.length} onClick={downloadByRating}>
            {t('teach_model.export_log')}
          </Button>
        </Group>
      </Group>

      <FeedbackTable
        data={tableData}
        actionCallBack={() => {
          init(false);
        }}
      />

      {tableLoading && (
        <Center className="h-[300px] bg-white flex items-center">
          <Loader />
        </Center>
      )}

      {!tableLoading && !tableData.length && (
        <Center className="h-[300px] bg-white flex items-center">
          <Text color="gray.5">{t('no_data')}</Text>
        </Center>
      )}

      {!!page.total && (
        <Group justify="space-between" mt={15}>
          <Text size={'sm'} c="gray.5">
            {t('teach_model.table.total_items', { number: page.total })}
          </Text>
          <Pagination
            value={page.page}
            size={'sm'}
            onChange={(value) => {
              setPage((state) => ({ ...state, page: value }));
            }}
            total={page.pages}
            color="violet.5"
          />
        </Group>
      )}
    </div>
  );
};

export default TeachModel;
